import { get } from 'svelte/store';
import { translationLanguage } from '@repo/stores/Language';
import { isEnglish } from './languages.js';

const CALLABLE_NAME = 'translateResourcePage';

/**
 * Creates the translation provider consumed by the PDF document viewer.
 *
 * @param {{ callable: (name: string) => (data: any) => Promise<any> }} deps
 * @returns {{
 *   translatePage: (resourceId: string, page: number, areas: any[]) => Promise<any[]>,
 *   translateStrings: (resourceId: string, texts: string[], langCode: string) => Promise<string[]>,
 * }}
 */
export function createTranslationProvider({ callable } = {}) {
    if (typeof callable !== 'function') {
        throw new Error('@repo/translation: createTranslationProvider requires a `callable` factory');
    }

    const pageCache = new Map();

    /**
     * Send a batch of texts for translation and return them in request order.
     */
    async function requestTranslations(resourceId, page, langCode, texts) {
        const filled = [];
        texts.forEach((text, index) => {
            if (text && text.trim()) filled.push({ index, text });
        });

        if (filled.length === 0) return texts.map(() => '');

        const response = await callable(CALLABLE_NAME)({
            resourceId,
            page,
            langCode,
            areas: filled.map(({ text }) => ({ text })),
        });

        const translated = response?.data?.areas ?? [];
        const out = texts.map(() => '');
        filled.forEach(({ index }, i) => {
            out[index] = translated[i]?.translatedText ?? '';
        });

        return out;
    }

    async function translatePage(resourceId, page, areas) {
        const langCode = get(translationLanguage);

        if (!resourceId || isEnglish(langCode)) return [];
        if (!Array.isArray(areas) || areas.length === 0) return [];

        const texts = areas.map((area) => area?.text ?? '');
        // Area count is part of the key: a re-extraction that splits text
        // differently must not be served a stale, misaligned result.
        const key = `${langCode}::${resourceId}::${page}::${areas.length}`;

        let pending = pageCache.get(key);
        if (!pending) {
            pending = requestTranslations(resourceId, page, langCode, texts);
            // A failed request must not be cached, or the page stays broken
            // for the rest of the session.
            pending.catch(() => pageCache.delete(key));
            pageCache.set(key, pending);
        }

        try {
            const translated = await pending;
            return areas.map((area, i) => ({ ...area, translatedText: translated[i] ?? '' }));
        } catch (err) {
            console.error('[translation] failed to translate page', err);
            return [];
        }
    }

    async function translateStrings(resourceId, texts, langCode) {
        if (!resourceId || isEnglish(langCode)) return texts;
        if (!Array.isArray(texts) || texts.length === 0) return [];

        try {
            const translated = await requestTranslations(resourceId, null, langCode, texts);
            // Fall back to the source string rather than blanking it out —
            // callers substitute the result straight into the text they read.
            return texts.map((text, i) => translated[i] || text);
        } catch (err) {
            console.error('[translation] failed to translate strings', err);
            return texts;
        }
    }

    return { translatePage, translateStrings };
}
